import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, Button, Grid, theme, Tooltip, Table, Radio, DatePicker } from "antd";
import { ArrowLeftOutlined, BarChartOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import { useAuth } from "../context/AuthContext";
import { getAlbumStats, isNoAccessError } from "./api";
import type { AlbumStats, ContributorStat, DayCount } from "./types";
import { AlbumNoAccess } from "./AlbumNoAccess";
import { chartPalette, EmptyState, ErrorState, LoadingState, PageHeader, PageSection, StatTile } from "@dowty/ui";

const { useBreakpoint } = Grid;
const { Text } = Typography;
const { RangePicker } = DatePicker;

const MAX_SERIES = 6;
const OTHER_LABEL = "Other";

/** What "most" means. Each is a real question someone asks of a shared album, and they genuinely
 *  disagree — the person with the most photos is rarely the one with the most recorded minutes. */
type Metric = "items" | "videos" | "photos" | "duration" | "size";

const METRICS: { value: Metric; label: string; of: (c: Totals) => number; format: (n: number) => string }[] = [
    { value: "items", label: "Items", of: c => c.photos + c.videos, format: String },
    { value: "videos", label: "Videos", of: c => c.videos, format: String },
    { value: "photos", label: "Photos", of: c => c.photos, format: String },
    { value: "duration", label: "Duration", of: c => c.durationMs, format: n => formatDuration(n) },
    { value: "size", label: "Size", of: c => c.bytes, format: n => formatBytes(n) },
];

type Totals = { photos: number; videos: number; bytes: number; durationMs: number };

function formatBytes(bytes: number): string {
    if (bytes <= 0) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, exponent);
    return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

function formatDuration(ms: number): string {
    if (ms <= 0) return "—";
    const totalMinutes = Math.round(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${Math.max(1, totalMinutes)} min`;
    return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

// One Intl formatter for the module rather than a toLocaleDateString call per label: a year-long album
// renders hundreds of day columns, and constructing the formatter is the expensive half of that call.
const DAY_LABEL_FORMAT = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", timeZone: "UTC" });
const formatDay = (isoDate: string): string => DAY_LABEL_FORMAT.format(new Date(`${isoDate}T00:00:00Z`));

const sumDays = (days: DayCount[]): Totals => days.reduce<Totals>(
    (acc, d) => ({
        photos: acc.photos + d.photos,
        videos: acc.videos + d.videos,
        bytes: acc.bytes + d.bytes,
        durationMs: acc.durationMs + d.durationMs,
    }),
    { photos: 0, videos: 0, bytes: 0, durationMs: 0 },
);

/** Rolls contributors past the palette's fixed slot count into a single "Other" row, so a hue is never
 *  generated or cycled. Their per-day breakdowns merge too, keeping the day chart honest. */
function capContributors(contributors: ContributorStat[]): ContributorStat[] {
    if (contributors.length <= MAX_SERIES) return contributors;

    const kept = contributors.slice(0, MAX_SERIES - 1);
    const rest = contributors.slice(MAX_SERIES - 1);
    const mergedDays = new Map<string, DayCount>();
    rest.forEach(c => c.days.forEach(d => {
        const existing = mergedDays.get(d.date);
        mergedDays.set(d.date, existing
            ? {
                date: d.date,
                photos: existing.photos + d.photos,
                videos: existing.videos + d.videos,
                bytes: existing.bytes + d.bytes,
                durationMs: existing.durationMs + d.durationMs,
            }
            : d);
    }));

    const days = Array.from(mergedDays.values()).sort((a, b) => a.date.localeCompare(b.date));
    const totals = sumDays(days);
    return [
        ...kept,
        {
            name: OTHER_LABEL,
            ...totals,
            firstEpochSec: Math.min(...rest.map(c => c.firstEpochSec)),
            lastEpochSec: Math.max(...rest.map(c => c.lastEpochSec)),
            days,
        },
    ];
}

/** Donut of each contributor's share of the selected metric. Drawn as dashed circle strokes rather than
 *  arc paths: the gap between segments comes free from the dash pattern, which is what keeps adjacent
 *  slices readable without relying on their colours differing enough. */
const Donut: React.FC<{
    slices: { name: string; value: number; color: string }[];
    total: number;
    centerLabel: string;
    centerSub: string;
}> = ({ slices, total, centerLabel, centerSub }) => {
    const { token } = theme.useToken();
    const radius = 56;
    const circumference = 2 * Math.PI * radius;
    const gap = 2;

    let offset = 0;
    return (
        <svg viewBox="0 0 160 160" style={{ width: 160, height: 160, flexShrink: 0 }} aria-hidden="true">
            <circle cx="80" cy="80" r={radius} fill="none" stroke={token.colorFillQuaternary} strokeWidth="16" />
            {total > 0 && slices.map((slice, index) => {
                const length = (slice.value / total) * circumference;
                const dash = Math.max(0, length - gap);
                const element = (
                    <circle
                        key={`${slice.name}-${index}`}
                        cx="80" cy="80" r={radius}
                        fill="none"
                        stroke={slice.color}
                        strokeWidth="16"
                        strokeDasharray={`${dash} ${circumference - dash}`}
                        strokeDashoffset={-offset}
                        // Start at 12 o'clock rather than 3, which is where a reader expects a donut to begin.
                        transform="rotate(-90 80 80)"
                    >
                        <title>{`${slice.name}: ${Math.round((slice.value / total) * 100)}%`}</title>
                    </circle>
                );
                offset += length;
                return element;
            })}
            <text x="80" y="76" textAnchor="middle" fontSize="18" fontWeight="600" fill={token.colorText}>
                {centerLabel}
            </text>
            <text x="80" y="94" textAnchor="middle" fontSize="11" fill={token.colorTextSecondary}>
                {centerSub}
            </text>
        </svg>
    );
};

const DayTooltipContent: React.FC<{
    date: string;
    byContributor: Map<string, number>;
    ranked: { name: string }[];
    activeMetric: typeof METRICS[number];
}> = ({ date, byContributor, ranked, activeMetric }) => (
    <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{formatDay(date)}</div>
        {ranked
            .filter(contributor => (byContributor.get(contributor.name) ?? 0) > 0)
            .map((contributor, index) => (
                <div key={`${contributor.name}-${index}`}>
                    {contributor.name}: {activeMetric.format(byContributor.get(contributor.name)!)}
                </div>
            ))}
    </div>
);

// A thunk, not an element: at most one tooltip is visible, so each day's subtree is built only when shown.
const dayTooltip = (props: React.ComponentProps<typeof DayTooltipContent>) => () => <DayTooltipContent {...props} />;

const DualcamStatsPage: React.FC = () => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const { albumId } = useParams<{ albumId: string }>();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token: designToken } = theme.useToken();
    const { user } = useAuth();

    const [stats, setStats] = useState<AlbumStats | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [statsError, setStatsError] = useState<string | null>(null);
    const [statsLoadAttempt, setStatsLoadAttempt] = useState(0);
    const [dayView, setDayView] = useState<"chart" | "table">("chart");
    const [metric, setMetric] = useState<Metric>("items");
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
    // Contributors the viewer has toggled off. Hidden rather than removed: they still count towards
    // nothing, but un-hiding must not need a refetch, so the underlying data stays loaded.
    const [hidden, setHidden] = useState<Set<string>>(new Set());

    // The app's light/dark is user-toggleable (App.tsx owns the algorithm), and this page isn't passed
    // that flag — derived from the surface token instead of duplicating the toggle's state, so the
    // series palette follows whatever the user actually picked rather than the OS preference.
    const isDark = useMemo(() => {
        const hex = designToken.colorBgContainer.replace("#", "");
        if (hex.length < 6) return false;
        const [r, g, b] = [0, 2, 4].map(i => Number.parseInt(hex.slice(i, i + 2), 16));
        return 0.299 * r + 0.587 * g + 0.114 * b < 128;
    }, [designToken.colorBgContainer]);
    const series = chartPalette(isDark ? "dark" : "light").slice(0, MAX_SERIES);

    // Depends on the token *string*, not the user object: AuthProvider replaces that object once on
    // mount after validateSession, and an object-identity dependency refired this effect mid-flight —
    // two full paginated NAS listings for one page view, the second one also missing the server cache.
    const token = user?.token;
    useEffect(() => {
        if (!token || !albumId) return;
        setNotFound(false);
        setStatsError(null);
        getAlbumStats(token, Number(albumId))
            .then(setStats)
            .catch(err => {
                if (isNoAccessError(err)) {
                    setNotFound(true);
                    return;
                }
                setStatsError((err as Error).message || (isGerman ? "Statistiken konnten nicht geladen werden." : "Failed to load statistics."));
            });
    }, [token, albumId, statsLoadAttempt, isGerman]);

    const capped = useMemo(() => capContributors(stats?.contributors ?? []), [stats]);

    // Colour follows the contributor, not their rank or visibility: keyed off a name-sorted order so a
    // contributor keeps their hue when the ranking metric changes or someone else is hidden. Anything
    // else would repaint the survivors every time a filter moved.
    const colorOf = useMemo(() => {
        const byName = new Map<string, string>();
        capped
            .filter(c => c.name !== OTHER_LABEL)
            .map(c => c.name)
            .sort((a, b) => a.localeCompare(b))
            .forEach((name, index) => byName.set(name, series[index % series.length]));
        return (name: string) => (name === OTHER_LABEL ? series[MAX_SERIES - 1] : byName.get(name) ?? series[0]);
    }, [capped, series]);

    // Everything below derives from the date range: each contributor keeps only their in-range days, and
    // their totals are re-summed from those rather than using the server's all-time figures.
    const inRange = useMemo(() => {
        const [from, to] = range ?? [];
        const fromKey = from?.format("YYYY-MM-DD");
        const toKey = to?.format("YYYY-MM-DD");

        return capped.map(contributor => {
            const days = contributor.days.filter(d =>
                (!fromKey || d.date >= fromKey) && (!toKey || d.date <= toKey));
            return { name: contributor.name, days, totals: sumDays(days) };
        });
    }, [capped, range]);

    const visible = useMemo(
        () => inRange.filter(c => !hidden.has(c.name) && (c.totals.photos + c.totals.videos) > 0),
        [inRange, hidden],
    );

    const activeMetric = METRICS.find(m => m.value === metric)!;

    const ranked = useMemo(
        () => [...visible].sort((a, b) => activeMetric.of(b.totals) - activeMetric.of(a.totals) || a.name.localeCompare(b.name)),
        [visible, activeMetric],
    );

    const metricTotal = ranked.reduce((sum, c) => sum + activeMetric.of(c.totals), 0);
    const shownTotals = ranked.reduce<Totals>(
        (acc, c) => ({
            photos: acc.photos + c.totals.photos,
            videos: acc.videos + c.totals.videos,
            bytes: acc.bytes + c.totals.bytes,
            durationMs: acc.durationMs + c.totals.durationMs,
        }),
        { photos: 0, videos: 0, bytes: 0, durationMs: 0 },
    );

    // The full span of activity, used to bound the picker so a range can't be dragged into empty time.
    const bounds = useMemo(() => {
        const dates = capped.flatMap(c => c.days.map(d => d.date)).sort();
        return dates.length > 0 ? { first: dates[0], last: dates[dates.length - 1] } : null;
    }, [capped]);

    const { days, perDay, maxDayTotal } = useMemo(() => {
        const lookup = new Map<string, Map<string, number>>();
        ranked.forEach(c => c.days.forEach(d => {
            const value = activeMetric.of(d);
            if (value <= 0) return;
            if (!lookup.has(d.date)) lookup.set(d.date, new Map());
            lookup.get(d.date)!.set(c.name, value);
        }));

        const sortedDays = Array.from(lookup.keys()).sort((a, b) => a.localeCompare(b));
        const busiest = Math.max(1, ...sortedDays.map(date =>
            Array.from(lookup.get(date)!.values()).reduce((sum, n) => sum + n, 0)));
        return { days: sortedDays, perDay: lookup, maxDayTotal: busiest };
    }, [ranked, activeMetric]);

    const toggleHidden = (name: string) => setHidden(prev => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });

    return (
        <div style={{ padding: isMobile ? "16px" : "32px 24px" }}>

            <PageHeader title="Contributions" extra={<Button aria-label="Back to album" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/dualcam/${albumId}`)} />} />

            {notFound && (
                <AlbumNoAccess icon={<BarChartOutlined style={{ fontSize: 48, color: designToken.colorTextQuaternary }} />} />
            )}
            {!notFound && statsError && <ErrorState message={statsError} retry={() => setStatsLoadAttempt(attempt => attempt + 1)} retryLabel={isGerman ? "Erneut versuchen" : "Retry"} />}
            {!notFound && !statsError && stats === null && (
                <LoadingState variant="stats" label="Loading contribution stats" />
            )}
            {!notFound && stats?.totalItems === 0 && (
                // Distinct from AlbumNoAccess above: an empty album is a normal state, not lost access.
                <EmptyState icon={<BarChartOutlined />} title="Nothing in this album yet, so there's nothing to count." />
            )}
            {!notFound && stats !== null && stats.totalItems > 0 && (
                <>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
                        <Radio.Group
                            aria-label={isGerman ? "Statistikwert" : "Statistics metric"}
                            optionType="button"
                            value={metric}
                            onChange={event => setMetric(event.target.value as Metric)}
                        >
                            {METRICS.map(m => <Radio.Button className="sf-touch-target" key={m.value} value={m.value}>{m.label}</Radio.Button>)}
                        </Radio.Group>
                        <RangePicker
                            aria-label={isGerman ? "Statistikzeitraum" : "Statistics date range"}
                            placeholder={isGerman ? ["Startdatum", "Enddatum"] : ["Start date", "End date"]}
                            allowClear
                            value={range}
                            onChange={value => setRange(value?.[0] && value[1] ? [value[0], value[1]] : null)}
                            // Bounded to the album's own span: a range outside it can only ever show nothing.
                            disabledDate={current => {
                                if (!bounds || !current) return false;
                                const day = current.format("YYYY-MM-DD");
                                return day < bounds.first || day > bounds.last;
                            }}
                        />
                        {range && <Button type="link" onClick={() => setRange(null)}>All time</Button>}
                    </div>

                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
                        <StatTile label="Items" value={String(shownTotals.photos + shownTotals.videos)} />
                        <StatTile label="Videos" value={String(shownTotals.videos)} />
                        <StatTile label="Photos" value={String(shownTotals.photos)} />
                        <StatTile label="Duration" value={formatDuration(shownTotals.durationMs)} />
                        <StatTile label="Size" value={formatBytes(shownTotals.bytes)} />
                    </div>

                    {ranked.length === 0 ? (
                        <Text type="secondary">
                            Nothing in {range ? "that date range" : "this album"}
                            {hidden.size > 0 && " for the contributors you're showing"}.
                        </Text>
                    ) : (
                        <PageSection title={`Share of ${activeMetric.label.toLowerCase()}`}>
                            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center", marginBottom: 32 }}>
                                <Donut
                                    slices={ranked.map(c => ({ name: c.name, value: activeMetric.of(c.totals), color: colorOf(c.name) }))}
                                    total={metricTotal}
                                    centerLabel={activeMetric.format(metricTotal)}
                                    centerSub={activeMetric.label.toLowerCase()}
                                />

                                <div style={{ flex: 1, minWidth: 240, display: "flex", flexDirection: "column", gap: 12 }}>
                                    {ranked.map((contributor, index) => {
                                        const value = activeMetric.of(contributor.totals);
                                        const share = metricTotal > 0 ? Math.round((value / metricTotal) * 100) : 0;
                                        return (
                                            <div key={`${contributor.name}-${index}`}>
                                                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                                                    {/* Colour is never the only carrier of identity: the name is always
                                                        written out next to its swatch. */}
                                                    <span style={{
                                                        width: 10, height: 10, borderRadius: 3,
                                                        background: colorOf(contributor.name), flexShrink: 0,
                                                    }} />
                                                    <Text strong style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {contributor.name}
                                                    </Text>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        {activeMetric.format(value)} · {share}%
                                                    </Text>
                                                </div>
                                                {/* Thin mark, rounded data-end, anchored to a common baseline. */}
                                                <div style={{ height: 8, background: designToken.colorFillQuaternary, borderRadius: 4 }}>
                                                    <div style={{
                                                        width: `${metricTotal > 0 ? Math.max(2, (value / metricTotal) * 100) : 0}%`,
                                                        height: "100%",
                                                        background: colorOf(contributor.name),
                                                        borderRadius: 4,
                                                    }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </PageSection>
                    )}

                    {/* Doubles as the legend: every contributor is listed whether shown or not, so hiding one
                        leaves an obvious way to bring it back rather than making it vanish from the page. */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 28 }}>
                        {inRange.map((contributor, index) => {
                            const isHidden = hidden.has(contributor.name);
                            return (
                                <Button
                                    key={`${contributor.name}-${index}`}
                                    size="small"
                                    onClick={() => toggleHidden(contributor.name)}
                                    style={{ opacity: isHidden ? 0.45 : 1 }}
                                >
                                    <span style={{
                                        display: "inline-block", width: 8, height: 8, borderRadius: 2, marginRight: 6,
                                        background: isHidden ? designToken.colorTextQuaternary : colorOf(contributor.name),
                                    }} />
                                    {contributor.name}
                                </Button>
                            );
                        })}
                    </div>

                    <PageSection
                        title={`${activeMetric.label} by day`}
                        actions={<Radio.Group aria-label={isGerman ? "Statistikansicht" : "Statistics display"} optionType="button" value={dayView} onChange={event => setDayView(event.target.value as "chart" | "table")}>
                            <Radio.Button className="sf-touch-target" value="chart">Chart</Radio.Button>
                            <Radio.Button className="sf-touch-target" value="table">Table</Radio.Button>
                        </Radio.Group>}
                    >

                    {days.length === 0 && (
                        <Text type="secondary">No activity to chart for this selection.</Text>
                    )}
                    {days.length > 0 && dayView === "chart" && (
                        <>
                            {/* Days scroll horizontally rather than compressing: a long-running album can
                                have far more days than fit, and squeezed 1px columns read as noise. */}
                            <div style={{ overflowX: "auto", paddingBottom: 4 }}>
                                <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 160, minWidth: "100%" }}>
                                    {days.map(date => {
                                        const byContributor = perDay.get(date) ?? new Map<string, number>();
                                        const total = Array.from(byContributor.values()).reduce((sum, n) => sum + n, 0);
                                        return (
                                            <Tooltip
                                                key={date}
                                                // A thunk, not an element: at most one tooltip is ever visible,
                                                // and building every day's subtree eagerly meant an
                                                // O(days × contributors) filter on every render.
                                                 title={dayTooltip({ date, byContributor, ranked, activeMetric })}
                                            >
                                                <div style={{
                                                    display: "flex", flexDirection: "column", justifyContent: "flex-end",
                                                    // A 2px gap between stacked segments, drawn in the surface colour.
                                                    gap: 2, minWidth: 18, flex: 1, height: "100%", cursor: "default",
                                                }}>
                                                    {ranked.map((contributor, index) => {
                                                        const value = byContributor.get(contributor.name) ?? 0;
                                                        if (value === 0) return null;
                                                        return (
                                                            <div
                                                                key={`${contributor.name}-${index}`}
                                                                style={{
                                                                    height: `${(value / maxDayTotal) * 100}%`,
                                                                    background: colorOf(contributor.name),
                                                                    borderRadius: 2,
                                                                    minHeight: 3,
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                    <Text type="secondary" style={{ fontSize: 12, textAlign: "center", marginTop: 4 }}>
                                                        {metric === "items" || metric === "videos" || metric === "photos" ? total : ""}
                                                    </Text>
                                                </div>
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>{formatDay(days[0])}</Text>
                                {days.length > 1 && <Text type="secondary" style={{ fontSize: 12 }}>{formatDay(days[days.length - 1])}</Text>}
                            </div>
                        </>
                    )}
                    {days.length > 0 && dayView !== "chart" && (
                        <Table
                            size="small"
                            pagination={false}
                            scroll={{ x: true }}
                            rowKey="date"
                            dataSource={days.map(date => ({
                                date,
                                ...Object.fromEntries(ranked.map((c, index) => [`contributor-${index}`, activeMetric.format(perDay.get(date)?.get(c.name) ?? 0)])),
                            }))}
                            columns={[
                                { key: "date", title: "Day", dataIndex: "date", fixed: "left" as const, render: (date: string) => formatDay(date) },
                                ...ranked.map((c, index) => ({ key: `contributor-${index}`, title: c.name, dataIndex: `contributor-${index}`, align: "right" as const })),
                            ]}
                        />
                    )}

                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 20 }}>
                        Uploads are credited using the name Synology puts on the file, so anything uploaded
                        outside dowtex — or by someone no longer sharing this album — counts towards the
                        album's owner. A recording made with both cameras counts once, with one half's duration.
                    </Text>
                    </PageSection>
                </>
            )}
        </div>
    );
};

export default DualcamStatsPage;
